/*
 * Copyright (C) 2025 to Present Applause App Quality, Inc. All rights reserved.
 */

import { Injectable } from '@angular/core';

/**
 * Service for converting strings to URL-safe slugs
 * Replaces the angular-slugify dependency with native TypeScript implementation
 */
@Injectable({
  providedIn: 'root',
})
export class SlugService {
  /**
   * Converts a string to a URL-safe slug
   * - Converts to lowercase
   * - Replaces spaces and underscores with hyphens
   * - Removes special characters (keeps alphanumeric and hyphens)
   * - Removes consecutive hyphens
   * - Trims leading and trailing hyphens
   *
   * @param text - The text to convert to a slug
   * @returns The slugified string
   *
   * @example
   * slugify('Hello World') => 'hello-world'
   * slugify('  Foo  Bar  ') => 'foo-bar'
   * slugify('Test@#$%123') => 'test123'
   * slugify('Multiple---Hyphens') => 'multiple-hyphens'
   */
  slugify(text: string): string {
    if (!text) {
      return '';
    }

    return text
      .toString()
      .toLowerCase()
      .trim()
      // Replace spaces and underscores with hyphens
      .replace(/[\s_]+/g, '-')
      // Remove all non-alphanumeric characters except hyphens
      .replace(/[^\w\-]+/g, '')
      // Replace multiple consecutive hyphens with single hyphen
      .replace(/\-\-+/g, '-')
      // Remove leading and trailing hyphens
      .replace(/^-+/, '')
      .replace(/-+$/, '');
  }
}
